(() => {
  if (window.customElements.get('custom-product-info')) return;

  const selections = new Map();

  class CustomProductInfo extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      // Cart property key for the orientation choice. Sourced from the on-page
      // "Turn" label (block setting orientation_label) so the cart matches the
      // product page and is consistent across all items. Default: 'Turn'.
      this.turnPropertyName = this.dataset.turnPropertyName || 'Turn';
      this.form =
        document.forms.namedItem(this.formId) ||
        document.getElementById(this.formId);
      this.variants = Array.from(this.querySelectorAll('[data-custom-product-variant]'));
      this.material = this.querySelector('[data-custom-product-material]');
      this.materialPanel = this.querySelector('[data-custom-product-material-panel]');
      this.selection = this.querySelector('[data-custom-product-selection]');
      this.fabricGrid = this.querySelector('[data-custom-product-fabric-grid]');
      this.description = this.querySelector('[data-custom-product-description]');
      this.fabricModal = this.querySelector('[data-custom-product-fabric-modal]');
      this.fabricModalTitle = this.querySelector('[data-custom-product-fabric-modal-title]');
      this.fabricModalImage = this.querySelector('[data-custom-product-fabric-modal-image]');
      this.fabricModalDescription = this.querySelector(
        '[data-custom-product-fabric-modal-description]'
      );
      this.colorFamily = this.querySelector('[data-custom-product-color-family]');
      this.colorCount = this.querySelector('[data-custom-product-color-count]');
      this.turn = this.querySelector('[data-custom-product-turn]');
      this.turn?.querySelector('[data-custom-product-turn-clear]')?.remove();
      this.orientationHelpButton = this.querySelector('[data-custom-product-orientation-help]');
      this.designTrigger = this.querySelector('[data-custom-product-design-trigger]');
      this.modeLabel = this.querySelector('[data-custom-product-mode-label]');
      this.standardInputs = Array.from(
        this.querySelectorAll('[data-custom-product-standard-option] input[data-option-position]')
      );
      this.turnVariants = Array.from(
        this.querySelectorAll('[data-custom-product-variant-meta]')
      );
      this.turnVariant = this.querySelector(
        `[data-custom-product-variant-meta][data-variant-id="${this.dataset.selectedVariantId}"]`
      );
      this.orientationFallback = {
        left:
          this.turnVariant?.dataset.leftImage ||
          this.turnVariants.find((variant) => variant.dataset.leftImage)?.dataset.leftImage ||
          '',
        right:
          this.turnVariant?.dataset.rightImage ||
          this.turnVariants.find((variant) => variant.dataset.rightImage)?.dataset.rightImage ||
          '',
      };
      this.selectedVariant = this.variants.find(
        (variant) => variant.dataset.variantId === this.dataset.selectedVariantId
      );
      this.pendingVariant = null;
      this.variantRequest = null;
      this.designRequested = Boolean(this.selectedVariant);

      this.addEventListener('click', this.onClick);
      this.designTrigger?.addEventListener('click', this.onDesignClick);
      this.orientationHelpButton?.addEventListener('click', this.onOrientationHelpClick);
      this.standardInputs.forEach((input) => input.addEventListener('change', this.onStandardChange));
      this.turn.addEventListener('click', this.onTurnClick);
      this.form?.addEventListener('variant:change', this.onVariantChange);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.designTrigger?.removeEventListener('click', this.onDesignClick);
      this.orientationHelpButton?.removeEventListener('click', this.onOrientationHelpClick);
      this.standardInputs?.forEach((input) => input.removeEventListener('change', this.onStandardChange));
      this.turn?.removeEventListener('click', this.onTurnClick);
      this.form?.removeEventListener('variant:change', this.onVariantChange);
      this.variantRequest?.abort();
    }

    onClick = (event) => {
      const fabricPreview = event.target.closest('[data-custom-product-fabric-preview]');
      if (fabricPreview) {
        this.populateFabricPreview(fabricPreview);
        return;
      }

      const standardOption = event.target.closest('[data-custom-product-standard-option]');
      if (standardOption) {
        this.exitDesign(standardOption.dataset.modeLabel);
        return;
      }

      const variantTrigger = event.target.closest('[data-custom-product-variant-trigger]');
      if (variantTrigger) {
        event.preventDefault();
        this.selectVariant(variantTrigger.closest('[data-custom-product-variant]'));
        // User picked a fabric type → gently reveal the colour grid below.
        this.scrollToSelection();
        return;
      }

      const fabricChoice = event.target.closest('[data-custom-product-fabric-choice]');
      if (fabricChoice) {
        event.preventDefault();
        this.selectFabric(fabricChoice);
        return;
      }

    };

    onDesignClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.startDesign();
    };

    onOrientationHelpClick = () => {
      this.syncOrientationHelpImages();
      requestAnimationFrame(() => this.syncOrientationHelpImages());
      window.setTimeout(() => this.syncOrientationHelpImages(), 150);
    };

    onStandardChange = (event) => {
      if (event.target.checked) {
        this.exitDesign(event.target.closest('[data-custom-product-standard-option]')?.dataset.modeLabel);
      }
    };

    onTurnClick = (event) => {
      const turnOption = event.target.closest('[data-custom-product-turn-option]');
      if (!turnOption) return;
      event.preventDefault();
      const direction = turnOption.dataset.customProductTurnOption;
      if (this.state.turn === direction) {
        this.clearTurnSelection();
      } else {
        this.selectTurn(direction);
      }
    };

    onVariantChange = (event) => {
      const variantId = event.detail?.variant?.id;
      if (!variantId) return;
      this.dataset.selectedVariantId = String(variantId);
      const pendingVariant = this.pendingVariant;
      this.turnVariant =
        this.querySelector(`[data-custom-product-variant-meta][data-variant-id="${variantId}"]`) ||
        pendingVariant;
      const matchedVariant = this.variants.find(
        (variant) => variant.dataset.variantId === String(variantId)
      );
      this.selectedVariant = matchedVariant || pendingVariant;
      this.pendingVariant = null;
      this.designRequested = Boolean(this.selectedVariant);
      const selectedStandardOption = this.querySelector(
        `[data-custom-product-standard-option][data-variant-id="${variantId}"]`
      );
      if (this.modeLabel) {
        this.modeLabel.textContent = this.designRequested
          ? this.designTrigger?.dataset.modeLabel || this.dataset.designLabel
          : selectedStandardOption?.dataset.modeLabel || this.modeLabel.textContent;
      }
      this.variants.forEach((variant) => {
        const input = variant.querySelector('input[data-option-position]');
        if (input) input.checked = variant === this.selectedVariant;
      });
      this.querySelectorAll('[data-custom-product-standard-option]').forEach((option) => {
        const input = option.querySelector('input[data-option-position]');
        if (input) input.checked = option.dataset.variantId === String(variantId);
      });
      this.closeMaterialPanel();
      this.render();
    };

    get stateKey() {
      return `${this.formId}:${this.dataset.selectedVariantId || 'none'}`;
    }

    get state() {
      if (!selections.has(this.stateKey)) selections.set(this.stateKey, {});
      return selections.get(this.stateKey);
    }

    startDesign() {
      this.designRequested = true;
      if (this.modeLabel) this.modeLabel.textContent = this.designTrigger?.dataset.modeLabel || this.dataset.designLabel;
      const firstAvailableVariant = this.variants.find(
        (variant) => !variant.querySelector('[data-custom-product-variant-trigger]')?.disabled
      );
      if (!this.selectedVariant && firstAvailableVariant) {
        this.selectVariant(firstAvailableVariant);
        return;
      }
      this.render();
    }

    exitDesign(label) {
      this.designRequested = false;
      this.selectedVariant = null;
      this.pendingVariant = null;
      this.variants.forEach((variant) => {
        const input = variant.querySelector('input[data-option-position]');
        if (input) input.checked = false;
        variant.classList.remove('is-selected');
      });
      if (this.modeLabel && label) this.modeLabel.textContent = label;
      this.render();
    }

    selectVariant(variant) {
      if (!variant) return;
      this.turnVariant = variant;
      const input = variant.querySelector('input[data-option-position]');
      if (!input || input.checked) {
        this.selectedVariant = variant;
        this.designRequested = true;
        this.render();
        return;
      }

      const nextKey = `${this.formId}:${variant.dataset.variantId}`;
      if (!selections.has(nextKey)) selections.set(nextKey, {});
      this.pendingVariant = variant;
      this.selectedVariant = variant;
      this.designRequested = true;
      this.render();

      // Use the same native change event as the theme's regular variant
      // controls. VariantPicker then re-renders price, media, availability,
      // and the form's variant ID from Shopify's selected combination.
      // NOTE: do NOT use input.click() here — the input is a visually-hidden
      // (sr-only) radio, and click() moves focus to it, which makes the browser
      // scroll it into view (the page "jumps" while choosing a fabric/colour).
      // Dispatching a bubbling 'change' triggers the exact same VariantPicker
      // handler (delegated on `input[data-option-position][form=...]`) without
      // moving focus, so the page stays put.
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      if (!this.getNativeVariantPicker()) {
        this.selectCombination();
      }
    }

    getNativeVariantPicker() {
      return document.querySelector(
        `variant-picker[form-id="${CSS.escape(this.formId)}"]`
      );
    }

    async selectCombination() {
      if (!this.form || !this.dataset.productHandle || !this.dataset.sectionId) return;

      const optionValues = Array.from(this.form.elements)
        .filter((item) => item.matches?.('input[data-option-position]:checked'))
        .sort(
          (first, second) =>
            Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition)
        )
        .map((input) => input.value);
      if (!optionValues.length) return;

      this.variantRequest?.abort();
      this.variantRequest = new AbortController();

      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:start', { bubbles: true })
        );

        const root = window.Shopify?.routes?.root || '/';
        const url =
          `${root}products/${encodeURIComponent(this.dataset.productHandle)}` +
          `?option_values=${encodeURIComponent(optionValues.join(','))}` +
          `&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        const response = await fetch(url, { signal: this.variantRequest.signal });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);

        const html = await response.text();
        const htmlFragment = document.createRange().createContextualFragment(html);
        const nextInfo = htmlFragment.querySelector(
          `custom-product-info[data-form-id="${CSS.escape(this.formId)}"]`
        );
        const variantJson = nextInfo
          ?.querySelector('[data-custom-product-selected-variant]')
          ?.textContent?.trim();
        const nextVariant = variantJson ? JSON.parse(variantJson) : null;
        if (!nextVariant?.id) throw new Error('Shopify did not resolve a fabric variant');

        const previousVariant = this.getSelectedVariantData();
        const variantIdInput = this.form.querySelector('[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = String(nextVariant.id);
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', nextVariant.id);
        window.history.replaceState({ path: nextUrl.toString() }, '', nextUrl.toString());

        this.form.dispatchEvent(
          new CustomEvent('product:rerender', {
            detail: { htmlFragment, productChange: false }
          })
        );
        this.form.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              formId: this.formId,
              variant: nextVariant,
              previousVariant
            }
          })
        );
        window.Shopify?.PaymentButton?.init();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[CustomProductInfo] Unable to select fabric variant', error);
        }
      } finally {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
      }
    }

    getSelectedVariantData() {
      const json = this.querySelector(
        '[data-custom-product-selected-variant]'
      )?.textContent?.trim();

      try {
        return json ? JSON.parse(json) : null;
      } catch {
        return null;
      }
    }

    render() {
      const hasSelectedDesignVariant = Boolean(this.selectedVariant);
      const designMode = hasSelectedDesignVariant || this.designRequested;
      const designTrigger = this.getDesignTrigger();
      designTrigger?.classList.toggle('is-selected', designMode);
      designTrigger?.setAttribute('aria-pressed', designMode ? 'true' : 'false');
      this.material.hidden = !designMode;
      this.selection.hidden = !hasSelectedDesignVariant;
      this.renderTurn();

      if (!designMode) {
        this.clearFabricProperties();
        return;
      }

      if (!hasSelectedDesignVariant) {
        this.clearFabricProperties();
        return;
      }

      this.variants.forEach((variant) => {
        variant.classList.toggle('is-selected', variant === this.selectedVariant);
      });
      this.renderDescription();
      this.renderFabrics();
    }

    getDesignTrigger() {
      return this.designTrigger;
    }

    closeMaterialPanel() {
      // The redesigned selector stays inline; kept as a no-op for rerender compatibility.
    }

    // Smoothly bring the colour grid into view after a fabric type is chosen.
    // Uses window.scrollTo with a sticky-header offset instead of
    // scrollIntoView({block:'start'}) so the colour heading isn't hidden under
    // the fixed top bar, and never targets the hidden radio (no jump).
    scrollToSelection() {
      const target = this.selection;
      if (!target) return;
      const run = () => {
        if (target.hidden) return;
        const offset = 110; // clearance for the sticky header
        const top = window.pageYOffset + target.getBoundingClientRect().top - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      };
      // Wait for layout after render(), then nudge again once async variant
      // resolution has settled so we land on the final position.
      requestAnimationFrame(run);
      window.setTimeout(run, 300);
    }

    renderDescription() {
      const text = this.selectedVariant.dataset.fabricDescription?.trim();
      this.description.replaceChildren();
      this.description.hidden = !text;
      if (!text) return;

      const heading = document.createElement('p');
      heading.className = 'h6';
      heading.textContent = 'Beskrivelse af stoffet';
      const copy = document.createElement('p');
      copy.textContent = text;
      this.description.append(heading, copy);
    }

    renderFabrics() {
      const fabricData = Array.from(
        this.selectedVariant.querySelectorAll('[data-custom-product-fabric-data]')
      );
      this.fabricGrid.replaceChildren();

      if (this.colorFamily) {
        this.colorFamily.textContent = this.getFabricFamily();
      }
      if (this.colorCount) this.colorCount.textContent = String(fabricData.length);

      fabricData.forEach((fabric, index) => {
        const card = document.createElement('div');
        card.className = 'custom-product-info__fabric-card-wrap';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'custom-product-info__fabric-card';
        button.dataset.customProductFabricChoice = '';
        button.dataset.fabricLabel = fabric.dataset.fabricLabel || '';
        button.dataset.fabricDisplayLabel = this.getColorDisplayLabel(fabric.dataset.fabricLabel || '');
        button.dataset.fabricImage = fabric.dataset.fabricImage || '';
        button.dataset.fabricImageLarge = fabric.dataset.fabricImageLarge || '';
        button.setAttribute('aria-pressed', 'false');

        if (button.dataset.fabricImage) {
          const image = document.createElement('img');
          image.src = button.dataset.fabricImage;
          image.alt = button.dataset.fabricDisplayLabel;
          image.loading = 'lazy';
          image.width = 300;
          image.height = 300;
          button.append(image);
        }

        const label = document.createElement('span');
        label.textContent = button.dataset.fabricDisplayLabel || `Stof ${index + 1}`;
        button.append(label);

        const preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'custom-product-info__fabric-preview';
        preview.dataset.customProductFabricPreview = '';
        preview.dataset.fabricDisplayLabel = button.dataset.fabricDisplayLabel;
        preview.dataset.fabricImage =
          button.dataset.fabricImageLarge || button.dataset.fabricImage;
        preview.setAttribute('aria-controls', this.fabricModal?.id || '');
        preview.setAttribute(
          'aria-label',
          `View ${button.dataset.fabricDisplayLabel || `fabric ${index + 1}`}`
        );
        preview.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="21" y1="3" x2="14" y2="10"></line>
            <polyline points="9 21 3 21 3 15"></polyline>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
        `;

        card.append(button, preview);
        this.fabricGrid.append(card);
      });

      const choices = Array.from(
        this.fabricGrid.querySelectorAll('[data-custom-product-fabric-choice]')
      );
      const saved = choices.find((choice) => choice.dataset.fabricLabel === this.state.fabricLabel);
      if (saved) {
        this.selectFabric(saved);
      } else if (choices[0]) {
        this.selectFabric(choices[0]);
      } else {
        this.setProperty('Valgt stof', this.selectedVariant.dataset.variantTitle || '');
        this.removeProperty('Billede af det valgte stof');
        this.updateModeLabel();
      }
    }

    populateFabricPreview(trigger) {
      if (!trigger) return;

      const name = trigger.dataset.fabricDisplayLabel || this.getFabricFamily();
      const image = trigger.dataset.fabricImage || '';
      const description = this.selectedVariant?.dataset.fabricDescription?.trim() || '';

      if (this.fabricModalTitle) this.fabricModalTitle.textContent = name;
      if (this.fabricModalImage) {
        this.fabricModalImage.src = image;
        this.fabricModalImage.alt = name;
        this.fabricModalImage.hidden = !image;
      }
      if (this.fabricModalDescription) {
        this.fabricModalDescription.textContent = description;
        this.fabricModalDescription.hidden = !description;
      }
    }

    selectFabric(choice) {
      if (!choice) return;
      this.fabricGrid.querySelectorAll('[data-custom-product-fabric-choice]').forEach((button) => {
        const selected = button === choice;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.state.fabricLabel = choice.dataset.fabricLabel || '';
      this.state.fabricImage = choice.dataset.fabricImageLarge || choice.dataset.fabricImage || '';
      const mainFabric = this.selectedVariant.dataset.variantTitle || '';
      const propertyValue = this.state.fabricLabel
        ? `${mainFabric} => ${this.state.fabricLabel}`
        : mainFabric;
      this.setProperty('Valgt stof', propertyValue);
      this.setProperty('Billede af det valgte stof', this.state.fabricImage);
      this.updateModeLabel();
    }

    updateModeLabel() {
      if (!this.modeLabel || !this.selectedVariant) return;
      const designLabel = this.designTrigger?.dataset.modeLabel || this.dataset.designLabel || 'Design Selv';
      const selectedColor = this.getColorDisplayLabel(this.state.fabricLabel);
      const family = this.selectedVariant.dataset.variantTitle || '';
      this.modeLabel.textContent = [designLabel, selectedColor || family].filter(Boolean).join(' · ');
    }

    getFabricFamily() {
      return (this.selectedVariant?.dataset.variantTitle || '').split('·')[0].trim();
    }

    getColorDisplayLabel(label) {
      const colorLabel = (label || '').trim();
      const family = this.getFabricFamily();
      const fabricName = family.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
      if (!colorLabel) return fabricName;
      if (
        !fabricName ||
        colorLabel.toLocaleLowerCase().startsWith(fabricName.toLocaleLowerCase())
      ) {
        return colorLabel;
      }
      return `${fabricName} ${colorLabel}`;
    }

    renderTurn() {
      const { left, right } = this.getTurnSources();
      this.syncOrientationHelpImages();
      this.turn.hidden = !left && !right;
      if (!left && !right) {
        this.removeProperty(this.turnPropertyName);
        this.removeProperty('_orientation_image');
        this.restoreGalleryImages();
        return;
      }

      ['left', 'right'].forEach((direction) => {
        const source = direction === 'left' ? left : right;
        const button = this.turn.querySelector(`[data-custom-product-turn-option="${direction}"]`);
        const image = button?.querySelector(`[data-custom-product-turn-image="${direction}"]`);
        if (!button || !image) return;
        button.hidden = !source;
        if (source) image.src = source;
      });

      let direction = this.state.turn;
      const hasSavedDirection =
        (direction === 'right' && right) || (direction === 'left' && left);

      if (!hasSavedDirection) {
        this.clearTurnSelection();
        return;
      }

      this.selectTurn(direction);
    }

    getTurnSources() {
      // Store-wide the orientation images are stored on the opposite metafields
      // (the left-facing photo lives in right_images and vice versa), so we swap
      // them here in the single place that feeds the turn buttons, the gallery
      // swap, the "which one?" help popup and the cart line-item image. This
      // makes the "Left-handed" choice show the true left-facing photo, etc.
      return {
        left: this.turnVariant?.dataset.rightImage || this.orientationFallback?.right || '',
        right: this.turnVariant?.dataset.leftImage || this.orientationFallback?.left || '',
      };
    }

    syncOrientationHelpImages() {
      const modalId = this.orientationHelpButton?.getAttribute('aria-controls');
      const modal = modalId ? document.getElementById(modalId) : null;
      if (!modal) return;

      const turnSources = this.getTurnSources();
      const sources = {
        left: turnSources.left || modal.dataset.orientationLeftImage || '',
        right: turnSources.right || modal.dataset.orientationRightImage || '',
      };

      Object.entries(sources).forEach(([direction, source]) => {
        if (!source) return;

        const slot = modal.querySelector(
          `[data-orientation-guide-image-slot="${direction}"]`
        );
        const guideImages = modal.querySelectorAll('.sofa-guide__billede');
        const image =
          slot?.querySelector('img') ||
          modal.querySelector(
            direction === 'right'
              ? 'img[alt="Højrevendt sofa"]'
              : 'img[alt="Venstrevendt sofa"]'
          ) ||
          guideImages[direction === 'right' ? 0 : 1];
        if (!image) return;

        image.src = source;
        image.srcset = source;
        image.loading = 'eager';
      });

      modal.dispatchEvent(
        new CustomEvent('sofa-orientation:images', {
          bubbles: true,
          detail: sources,
        })
      );
    }

    selectTurn(direction) {
      const source = this.getTurnSources()[direction];
      if (!source) return;
      this.state.turn = direction;
      const selectedButton = this.turn.querySelector(`[data-custom-product-turn-option="${direction}"]`);
      const label = selectedButton?.dataset.turnLabel || (direction === 'left' ? 'Venstrevendt' : 'Højrevendt');
      const turnLabel = this.turn.querySelector('[data-custom-product-turn-label]');
      if (turnLabel) turnLabel.textContent = label;
      this.turn.querySelectorAll('[data-custom-product-turn-option]').forEach((button) => {
        const selected = button.dataset.customProductTurnOption === direction;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.setProperty(this.turnPropertyName, label);
      // Store the chosen orientation image so the cart line item can show it
      // instead of the product's main image. Underscore-prefixed key = hidden
      // from the customer's property list but still submitted with the order.
      this.setProperty('_orientation_image', source);
      requestAnimationFrame(() => this.updateGalleryImage(source));
    }

    clearTurnSelection() {
      this.state.turn = '';
      const turnLabel = this.turn.querySelector('[data-custom-product-turn-label]');
      if (turnLabel) turnLabel.textContent = '';
      this.turn.querySelectorAll('[data-custom-product-turn-option]').forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-pressed', 'false');
      });
      this.removeProperty(this.turnPropertyName);
      this.removeProperty('_orientation_image');
      this.restoreGalleryImages();
    }

    updateGalleryImage(source) {
      const gallery = this.closest('product-rerender')?.querySelector('product-gallery');
      this.restoreGalleryImages();
      const selectedCell = gallery?.carousel?.selectedCell;
      const image =
        selectedCell?.querySelector('img') ||
        gallery?.querySelector('.product-gallery__media:not([hidden]) img');
      if (!image) return;
      if (!image.dataset.customProductOriginalSrc) {
        image.dataset.customProductOriginalSrc = image.getAttribute('src') || '';
        image.dataset.customProductOriginalSrcset = image.getAttribute('srcset') || '';
      }
      image.src = source;
      image.srcset = source;
    }

    restoreGalleryImages() {
      const gallery = this.closest('product-rerender')?.querySelector('product-gallery');
      gallery?.querySelectorAll('[data-custom-product-original-src]').forEach((image) => {
        image.setAttribute('src', image.dataset.customProductOriginalSrc);
        if (image.dataset.customProductOriginalSrcset) {
          image.setAttribute('srcset', image.dataset.customProductOriginalSrcset);
        } else {
          image.removeAttribute('srcset');
        }
        delete image.dataset.customProductOriginalSrc;
        delete image.dataset.customProductOriginalSrcset;
      });
    }

    setProperty(name, value) {
      if (!this.form) return;
      let input = this.form.querySelector(`input[data-custom-product-property="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.customProductProperty = name;
        this.form.append(input);
      }
      input.value = value || '';
      input.disabled = !value;
    }

    removeProperty(name) {
      this.form?.querySelector(`input[data-custom-product-property="${name}"]`)?.remove();
    }

    clearFabricProperties() {
      this.removeProperty('Valgt stof');
      this.removeProperty('Billede af det valgte stof');
    }
  }

  window.customElements.define('custom-product-info', CustomProductInfo);
})();